import { getCanvasState } from '@components/ArtBoard/Canvas/helpers/canvas'
import { ELEMENT_GESTURE_MOVABLE_CONTAINER } from '@components/ArtBoard/Canvas/hooks/events/useDragEvents'
import type { EventChannel } from 'redux-saga'
import { call, put, race, take } from 'redux-saga/effects'

import { GlobalActions } from '@/store'
import {
  createFrameChannel,
  distance,
  getCenteredTranslate,
  getJumpDuration,
  interpolateTranslate,
  JUMP_MIN_DISTANCE_PX,
  prefersReducedMotion,
} from './utils'

type Action = ReturnType<typeof GlobalActions.jumpTo>

interface Tick {
  frame: number
  interrupted: ReturnType<typeof GlobalActions.cancelJump>
}

export function* jumpTo({ payload: { value, selector } }: Action) {
  const ref = document.getElementById(ELEMENT_GESTURE_MOVABLE_CONTAINER)

  const query = `[${selector}="${value}"]`

  const page = document.querySelector(query)
  const rect = page?.getBoundingClientRect()

  if (!rect) {
    return
  }

  const { translate: from } = getCanvasState(ref)

  const to = getCenteredTranslate(rect, from, {
    width: window.innerWidth,
    height: window.innerHeight,
  })

  const reduced: boolean = yield call(prefersReducedMotion)

  if (reduced || distance(from, to) < JUMP_MIN_DISTANCE_PX) {
    yield put(GlobalActions.updateCanvas({ translate: to }))
    return
  }

  yield call(animateTranslate, from, to)
}

// Moves the canvas frame by frame through updateCanvas, so the container
// style, the tab's session state and the `canvas` window event all stay in
// step with what is on screen. Stops early when a gesture takes over; the
// finally block also runs when takeLatest cancels us for a newer jump.
function* animateTranslate(from: number[], to: number[]) {
  const duration = getJumpDuration(distance(from, to))
  const frames: EventChannel<number> = yield call(createFrameChannel)

  // The clock starts on the first frame the browser actually delivers, not
  // when the jump was requested: on a big board the first frame after a click
  // can come a second later (layout and raster, not JS), and a clock started
  // earlier would make the animation snap most of the way at once.
  let startedAt: number | null = null

  try {
    while (true) {
      const { frame, interrupted }: Partial<Tick> = yield race({
        frame: take(frames),
        interrupted: take(GlobalActions.cancelJump),
      })

      if (interrupted || frame === undefined) {
        return
      }

      if (startedAt === null) {
        startedAt = frame
      }

      const progress = Math.min((frame - startedAt) / duration, 1)

      yield put(
        GlobalActions.updateCanvas({
          translate: interpolateTranslate(from, to, progress),
        })
      )

      if (progress === 1) {
        return
      }
    }
  } finally {
    frames.close()
  }
}
